import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Download, Sparkles, Loader2, RotateCcw, Upload } from "lucide-react";
import { adminFetch } from "@/lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_SUBJECTS = Array(8).fill("");
const EMPTY_GRADES = Array(8).fill("");
const EMPTY_COMMENTS = Array(4).fill("");

const PHOTO_DEFAULTS = { zoom: 1.0, xOffset: 0, yOffset: 0, rotation: 0 };
const PET_NAME_Y_DEFAULT = 0;

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#6E6880", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.6 };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #E3E0EC", borderRadius: 9, fontSize: 14, fontFamily: "inherit", background: "#fff", outline: "none", color: "#2A2540", boxSizing: "border-box" };
const cardStyle = { background: "#fff", border: "1px solid #ECEAF3", borderRadius: 14, padding: "18px 20px" };

function resolvePhotoUrl(fileUrl) {
  if (!fileUrl) return "";
  return fileUrl.startsWith("http") ? fileUrl : `${API}${fileUrl}`;
}

export function resolveReportPetName(report, savedReview = {}, savedApproved = {}) {
  const questionnaireNickname = String(report?.questionnaire?.text?.nicknames || "").trim();
  return (savedReview.pet_name || questionnaireNickname || savedApproved.pet_name || report?.pet_name || "").toUpperCase();
}

export default function ReportPreview() {
  const { orderNumber, reportId } = useParams();
  const navigate = useNavigate();
  const [petName, setPetName] = useState("");
  const [petNameY, setPetNameY] = useState(PET_NAME_Y_DEFAULT);
  const [subjects, setSubjects] = useState([...EMPTY_SUBJECTS]);
  const [grades, setGrades] = useState([...EMPTY_GRADES]);
  const [comments, setComments] = useState([...EMPTY_COMMENTS]);
  const [creativeMeta, setCreativeMeta] = useState({ personalized_greeting: "", notes_text: "", award_wording: "" });
  const [mustInclude, setMustInclude] = useState("");
  const [questionnaire, setQuestionnaire] = useState({});
  const [mustIncludeVerified, setMustIncludeVerified] = useState(false);
  const [noteFont, setNoteFont] = useState("comic");
  const [noteFontSizes, setNoteFontSizes] = useState([38, 38, 38, 38]);
  const [noteColor, setNoteColor] = useState("#C70F21");
  const [noteUnderlines, setNoteUnderlines] = useState([true, true, true, false]);

  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFilename, setPhotoFilename] = useState("");
  const [photoSaving, setPhotoSaving] = useState(false);

  const [photoZoom, setPhotoZoom] = useState(PHOTO_DEFAULTS.zoom);
  const [photoX, setPhotoX] = useState(PHOTO_DEFAULTS.xOffset);
  const [photoY, setPhotoY] = useState(PHOTO_DEFAULTS.yOffset);
  const [photoRot, setPhotoRot] = useState(PHOTO_DEFAULTS.rotation);

  const [renderedUrl, setRenderedUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftNotice, setDraftNotice] = useState("");
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderNumber || !reportId) return;
    let cancelled = false;

    async function loadCreativeDraft() {
      setError(null);
      try {
        const response = await adminFetch(`${API}/api/orders/${encodeURIComponent(orderNumber)}`);
        if (!response.ok) throw new Error(`Order fetch failed (${response.status})`);
        const order = await response.json();
        const report = (order.reports || []).find((item) => String(item.id) === String(reportId));
        if (!report) throw new Error("Selected report was not found in this order.");
        const creative = report.creative_output || {};
        const savedReview = report.creative_review || {};
        const savedApproved = report.approved_package || {};
        const finalSubjects = creative.final_subjects || creative.subject_candidates || [];
        const finalComments = creative.final_teacher_comments || creative.teacher_comment_candidates || [];
        if (finalSubjects.length !== 8 || finalComments.length !== 4) {
          throw new Error("Selected report does not contain exactly 8 subjects and 4 teacher comments.");
        }
        if (cancelled) return;

        const photos = report.photos || [];
        const requiredWording = String(report.questionnaire?.must_include || "").trim();
        const savedPetName = resolveReportPetName(report, savedReview, savedApproved);
        const savedPhotoId = savedReview.selected_photo_id || savedApproved.selected_photo_id || report.selected_photo_id || "";
        const savedFontSizes = Array.isArray(savedReview.note_font_sizes) && savedReview.note_font_sizes.length === 4
          ? savedReview.note_font_sizes
          : [38, 38, 38, 38];
        const savedUnderlines = Array.isArray(savedReview.note_underlines) && savedReview.note_underlines.length === 4
          ? savedReview.note_underlines
          : [true, true, true, false];

        setPetName(savedPetName);
        setPetNameY(Number(savedReview.pet_name_y_offset ?? PET_NAME_Y_DEFAULT));
        setSubjects(finalSubjects.map((item) => typeof item === "string" ? item : (item.subject || "")));
        setGrades(finalSubjects.map((item) => typeof item === "string" ? "" : (item.grade || "")));
        setComments(finalComments.map((item) => typeof item === "string" ? item : (item.comment || "")));
        setCreativeMeta({
          personalized_greeting: savedReview.personalized_greeting || creative.personalized_greeting || `Dear ${savedPetName},`,
          notes_text: savedReview.notes_text || creative.notes_text || "",
          award_wording: savedReview.award_wording || creative.award_wording || "Top Student Report!",
        });
        setMustInclude(requiredWording);
        setQuestionnaire(report.questionnaire || {});
        setMustIncludeVerified(requiredWording ? Boolean(savedReview.must_include_verified) : true);
        setNoteFont(savedReview.note_font || "comic");
        setNoteFontSizes(savedFontSizes.map((value) => Number(value) || 38));
        setNoteColor(savedReview.note_color || "#C70F21");
        setNoteUnderlines(savedUnderlines.map(Boolean));
        setPhotoZoom(Number(savedReview.photo_zoom ?? PHOTO_DEFAULTS.zoom));
        setPhotoX(Number(savedReview.photo_x_offset ?? PHOTO_DEFAULTS.xOffset));
        setPhotoY(Number(savedReview.photo_y_offset ?? PHOTO_DEFAULTS.yOffset));
        setPhotoRot(Number(savedReview.photo_rotation ?? PHOTO_DEFAULTS.rotation));
        setUploadedPhotos(photos.map((photo) => ({ ...photo, selected_for_report: photo.photo_id === savedPhotoId ? 1 : 0 })));

        const selected = photos.find((photo) => photo.photo_id === savedPhotoId);
        if (selected?.file_url) {
          setSelectedPhotoId(selected.photo_id);
          setPhotoPreview(resolvePhotoUrl(selected.file_url));
          setPhotoFilename(selected.file_url.split("/").pop() || "");
        } else {
          setSelectedPhotoId("");
          setPhotoPreview(null);
          setPhotoFilename("");
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      }
    }

    loadCreativeDraft();
    return () => { cancelled = true; };
  }, [orderNumber, reportId]);

  const updateAt = (arr, setter) => (i, v) => {
    const next = [...arr];
    next[i] = v;
    setter(next);
  };
  const setSubject = updateAt(subjects, setSubjects);
  const setGrade = updateAt(grades, setGrades);
  const setComment = updateAt(comments, setComments);
  const setUnderline = (i, checked) => {
    const next = [...noteUnderlines];
    next[i] = checked;
    setNoteUnderlines(next);
  };
  const setCommentFontSize = (i, value) => {
    const next = [...noteFontSizes];
    next[i] = Math.max(24, Math.min(60, Number(value) || 24));
    setNoteFontSizes(next);
    setRenderedUrl(null);
  };

  async function chooseProductionPhoto(photo) {
    if (!photo?.photo_id || !photo?.file_url || photoSaving || selectedPhotoId === photo.photo_id) return;
    setPhotoSaving(true);
    setError(null);
    try {
      if (photo.quality_status !== "Acceptable") {
        const review = await adminFetch(`${API}/api/reports/${reportId}/request-new-photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: `__PHOTO_REVIEW__|${photo.photo_id}|Acceptable` }),
        });
        if (!review.ok) {
          const j = await review.json().catch(() => ({}));
          throw new Error(j.detail || `Could not select photo (${review.status})`);
        }
      }

      const select = await adminFetch(`${API}/api/reports/${reportId}/request-new-photos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: `__SELECT_PRODUCTION_PHOTO__|${photo.photo_id}` }),
      });
      if (!select.ok) {
        const j = await select.json().catch(() => ({}));
        throw new Error(j.detail || `Could not select photo (${select.status})`);
      }

      setSelectedPhotoId(photo.photo_id);
      setPhotoPreview(resolvePhotoUrl(photo.file_url));
      setPhotoFilename(photo.file_url.split("/").pop() || "");
      setPhotoZoom(PHOTO_DEFAULTS.zoom);
      setPhotoX(PHOTO_DEFAULTS.xOffset);
      setPhotoY(PHOTO_DEFAULTS.yOffset);
      setPhotoRot(PHOTO_DEFAULTS.rotation);
      setRenderedUrl(null);
      setUploadedPhotos((current) => current.map((p) => ({
        ...p,
        quality_status: p.photo_id === photo.photo_id ? "Acceptable" : p.quality_status,
        selected_for_report: p.photo_id === photo.photo_id ? 1 : 0,
      })));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setPhotoSaving(false);
    }
  }

  function downloadSelectedPhoto() {
    const selected = uploadedPhotos.find((photo) => photo.photo_id === selectedPhotoId);
    if (!selected?.file_url) return;
    setError(null);
    const url = resolvePhotoUrl(selected.file_url);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function replaceProductionPhoto(file) {
    if (!file || photoSaving) return;
    setPhotoSaving(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const upload = await adminFetch(`${API}/api/uploads/photos`, {
        method: "POST",
        body: formData,
      });
      const uploaded = await upload.json().catch(() => ({}));
      if (!upload.ok || !uploaded.file_url) {
        throw new Error(uploaded.detail || `Edited photo upload failed (${upload.status})`);
      }

      const filename = uploaded.file_url.split("/").pop() || "";
      if (!filename) throw new Error("Edited photo filename was not returned by the server.");

      const adopt = await adminFetch(`${API}/api/reports/${reportId}/request-new-photos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: `__ADOPT_PREVIEW_PHOTO__|${filename}` }),
      });
      const adopted = await adopt.json().catch(() => ({}));
      if (!adopt.ok) throw new Error(adopted.detail || `Could not attach edited photo (${adopt.status})`);

      const newPhotoId = adopted.selected_photo_id || filename;
      const newFileUrl = adopted.file_url || uploaded.file_url;
      const newPhoto = {
        photo_id: newPhotoId,
        file_url: newFileUrl,
        quality_status: "Acceptable",
        selected_for_report: 1,
      };

      setUploadedPhotos((current) => [
        ...current.map((photo) => ({ ...photo, selected_for_report: 0 })),
        newPhoto,
      ]);
      setSelectedPhotoId(newPhotoId);
      setPhotoPreview(resolvePhotoUrl(newFileUrl));
      setPhotoFilename(newFileUrl.split("/").pop() || "");
      setPhotoZoom(PHOTO_DEFAULTS.zoom);
      setPhotoX(PHOTO_DEFAULTS.xOffset);
      setPhotoY(PHOTO_DEFAULTS.yOffset);
      setPhotoRot(PHOTO_DEFAULTS.rotation);
      setRenderedUrl(null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setPhotoSaving(false);
    }
  }

  async function onRender(petNameYOffset = petNameY) {
    setBusy(true); setError(null);
    try {
      const styledComments = comments.map((text, i) => ({
        text,
        font: noteFont,
        size: noteFontSizes[i],
        color: noteColor,
        underline: noteUnderlines[i],
      }));
      const resp = await adminFetch(`${API}/api/reports/render`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pet_name: { text: petName, y_offset: petNameYOffset },
          subjects, grades, comments: styledComments,
          photo_filename: photoFilename || null,
          photo_zoom: photoZoom,
          photo_x_offset: photoX,
          photo_y_offset: photoY,
          photo_rotation: photoRot,
        }),
      });
      if (!resp.ok) {
        let detail = "";
        try {
          const j = await resp.clone().json();
          detail = j.detail || "";
        } catch { }
        if (!detail) detail = await resp.text();
        throw new Error(detail || `Render failed (${resp.status})`);
      }
      const blob = await resp.blob();
      if (renderedUrl) URL.revokeObjectURL(renderedUrl);
      setRenderedUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  function reviewPayload(selectedPhoto = selectedPhotoId) {
    return {
      final_subjects: subjects.map((subject, i) => ({ subject: subject.trim(), grade: grades[i].trim() })),
      final_teacher_comments: comments.map((comment) => comment.trim()),
      selected_photo_id: selectedPhoto || "",
      personalized_greeting: creativeMeta.personalized_greeting || `Dear ${petName},`,
      notes_text: creativeMeta.notes_text || "",
      award_wording: creativeMeta.award_wording || "Top Student Report!",
      approved_by: "Val",
      must_include_verified: Boolean(mustIncludeVerified),
      pet_name: petName,
      pet_name_y_offset: petNameY,
      photo_zoom: photoZoom,
      photo_x_offset: photoX,
      photo_y_offset: photoY,
      photo_rotation: photoRot,
      note_font: noteFont,
      note_font_sizes: noteFontSizes,
      note_color: noteColor,
      note_underlines: noteUnderlines,
    };
  }

  async function saveDraft() {
    if (draftSaving || approving || busy) return;
    setDraftSaving(true);
    setDraftNotice("");
    setError(null);
    try {
      const resp = await adminFetch(`${API}/api/reports/${reportId}/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewPayload()),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || `Draft save failed (${resp.status})`);
      setDraftNotice("Draft saved. You can safely leave this order and return later.");
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setDraftSaving(false);
    }
  }

  async function approveAndContinue() {
    if (!renderedUrl || !selectedPhotoId || approving || (mustInclude && !mustIncludeVerified)) return;
    setApproving(true);
    setError(null);
    try {
      const resp = await adminFetch(`${API}/api/reports/${reportId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reviewPayload()),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || `Approval failed (${resp.status})`);
      navigate(`/admin/orders/${encodeURIComponent(orderNumber)}`);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setApproving(false);
    }
  }

  const approvalBlocked = !renderedUrl || approving || busy || Boolean(mustInclude && !mustIncludeVerified);

  return (
    <main style={{ padding: "26px 30px", fontFamily: "'Nunito', sans-serif", color: "#2A2540" }} data-testid="report-preview-page">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0 }}>Report Preview</h1>
          <p style={{ color: "#8A8699", margin: "4px 0 0", fontSize: 14.5 }}>Order {orderNumber}</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={saveDraft} disabled={draftSaving || approving || busy} data-testid="save-report-draft-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: draftSaving || approving || busy ? "#C9C5D6" : "#203A6B", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 11, fontWeight: 800, fontSize: 14.5, cursor: draftSaving || approving || busy ? "default" : "pointer", fontFamily: "inherit" }}>
            {draftSaving ? <Loader2 size={17} className="animate-spin" /> : null}
            {draftSaving ? "Saving…" : "Save Draft"}
          </button>
          <button onClick={() => onRender()} disabled={busy || approving} data-testid="render-preview-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: busy || approving ? "#B7A9E8" : "#6A4FC0", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 11, fontWeight: 800, fontSize: 14.5, cursor: busy || approving ? "default" : "pointer", fontFamily: "inherit" }}>
            {busy ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
            {busy ? "Rendering…" : "Render Preview"}
          </button>
          <button onClick={approveAndContinue} disabled={approvalBlocked} data-testid="approve-report-btn" title={mustInclude && !mustIncludeVerified ? "Verify the Must Include wording before approval" : ""} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: approvalBlocked ? "#C9C5D6" : "#2F7A50", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 11, fontWeight: 800, fontSize: 14.5, cursor: approvalBlocked ? "default" : "pointer", fontFamily: "inherit" }}>
            {approving ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
            {approving ? "Approving…" : "Approve Report & Continue"}
          </button>
        </div>
      </div>

      {draftNotice && <div data-testid="draft-saved-notice" style={{ marginBottom: 16, padding: "11px 14px", background: "#EAF7EF", border: "1px solid #A9D5B8", borderRadius: 10, color: "#235D3C", fontSize: 13.5, fontWeight: 700 }}>{draftNotice}</div>}

      {mustInclude && (
        <div data-testid="must-include-alert" style={{ marginBottom: 18, padding: "14px 16px", background: "#FFF3C4", border: "2px solid #E7B633", borderRadius: 12, color: "#6B4300" }}>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 5 }}>Must Include</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>{mustInclude}</div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 13.5, fontWeight: 700 }}>
            <input type="checkbox" checked={mustIncludeVerified} onChange={(e) => setMustIncludeVerified(e.target.checked)} data-testid="must-include-verified" style={{ width: 18, height: 18, marginTop: 1 }} />
            <span>I verified this required customer content is represented in the final report.</span>
          </label>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 420px) minmax(0, 1fr)", gap: 22, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={cardStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 12px" }}>Pet</h2>
            <label style={labelStyle}>Pet name</label>
            <input data-testid="preview-pet-name" value={petName} onChange={(e) => { setPetName(e.target.value.toUpperCase()); setRenderedUrl(null); }} style={inputStyle} maxLength={40} />
            <div style={{ height: 12 }} />
            <SliderRow label="Pet name vertical position" testid="preview-pet-name-y" value={petNameY} onChange={(v) => { const hadPreview = Boolean(renderedUrl); setPetNameY(v); if (hadPreview) onRender(v); }} min={-30} max={30} step={1} suffix="%" />
            <div style={{ height: 6 }} />
            <label style={labelStyle}>Choose report photo</label>
            {uploadedPhotos.length === 0 ? (
              <div style={{ padding: 12, border: "1px solid #ECEAF3", borderRadius: 9, color: "#8A8699", fontSize: 13 }}>No uploaded photos are attached to this report.</div>
            ) : (
              <div data-testid="production-photo-picker" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                {uploadedPhotos.map((photo, i) => {
                  const selected = selectedPhotoId === photo.photo_id;
                  return (
                    <label key={photo.photo_id || i} style={{ position: "relative", padding: 6, borderRadius: 10, border: selected ? "3px solid #6A4FC0" : "1px solid #D8D3E6", background: selected ? "#F2EFFB" : "#fff", cursor: photoSaving ? "wait" : "pointer" }}>
                      <input type="checkbox" checked={selected} disabled={photoSaving} onChange={() => chooseProductionPhoto(photo)} aria-label={`Use Photo ${i + 1} for report`} style={{ position: "absolute", top: 10, left: 10, width: 20, height: 20, zIndex: 2 }} />
                      <img src={resolvePhotoUrl(photo.file_url)} alt={`${petName} option ${i + 1}`} style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 7, display: "block", background: "#F6F4FA" }} />
                      <div style={{ marginTop: 6, fontSize: 11.5, fontWeight: 700, color: selected ? "#5B21B6" : "#6E6880" }}>Photo {i + 1}</div>
                    </label>
                  );
                })}
              </div>
            )}
            {photoSaving && <div style={{ marginTop: 8, fontSize: 12.5, color: "#6A4FC0", fontWeight: 700 }}><Loader2 size={14} className="animate-spin" style={{ verticalAlign: "middle", marginRight: 6 }} />Saving photo…</div>}
          </div>

          <div style={cardStyle} data-testid="photo-controls-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Photo Controls</h2>
              <button type="button" data-testid="reset-photo-btn" onClick={() => { setPhotoZoom(PHOTO_DEFAULTS.zoom); setPhotoX(PHOTO_DEFAULTS.xOffset); setPhotoY(PHOTO_DEFAULTS.yOffset); setPhotoRot(PHOTO_DEFAULTS.rotation); setRenderedUrl(null); }} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#F2EFFB", color: "#6A4FC0", border: "none", padding: "6px 11px", borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>
                <RotateCcw size={13} /> Reset to default
              </button>
            </div>
            {photoPreview && (
              <>
                <div data-testid="live-photo-editor" style={{ width: "100%", height: 260, overflow: "hidden", position: "relative", marginBottom: 12, borderRadius: 8, border: "1px solid #ECEAF3", background: "#FAF9FE" }}>
                  <img src={photoPreview} alt="Selected production pet" style={{ position: "absolute", left: "50%", top: "50%", width: "100%", height: "100%", objectFit: "cover", transformOrigin: "center center", transform: `translate(-50%, -50%) translate(${photoX}%, ${photoY}%) rotate(${photoRot}deg) scale(${photoZoom})`, transition: "transform 80ms linear" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                  <button type="button" data-testid="download-production-photo" onClick={downloadSelectedPhoto} disabled={photoSaving} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#F2EFFB", color: "#5B21B6", border: "1px solid #C9BFEE", padding: "9px 10px", borderRadius: 9, fontWeight: 800, fontSize: 12.5, cursor: photoSaving ? "default" : "pointer", fontFamily: "inherit" }}>
                    <Download size={14} /> Download Original Photo
                  </button>
                  <label data-testid="replace-production-photo" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: photoSaving ? "#E6E3ED" : "#2F7A50", color: "#fff", border: "1px solid #2F7A50", padding: "9px 10px", borderRadius: 9, fontWeight: 800, fontSize: 12.5, cursor: photoSaving ? "default" : "pointer" }}>
                    {photoSaving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {photoSaving ? "Uploading…" : "Replace Production Photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" disabled={photoSaving} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) replaceProductionPhoto(file); }} style={{ display: "none" }} />
                  </label>
                </div>
              </>
            )}
            <SliderRow label="Zoom" testid="preview-photo-zoom" value={photoZoom} onChange={(v) => { setPhotoZoom(v); setRenderedUrl(null); }} min={1.0} max={3.0} step={0.05} suffix="×" decimals={2} />
            <SliderRow label="X position" testid="preview-photo-x" value={photoX} onChange={(v) => { setPhotoX(v); setRenderedUrl(null); }} min={-50} max={50} step={1} suffix="%" />
            <SliderRow label="Y position" testid="preview-photo-y" value={photoY} onChange={(v) => { setPhotoY(v); setRenderedUrl(null); }} min={-50} max={50} step={1} suffix="%" />
            <SliderRow label="Rotation" testid="preview-photo-rotation" value={photoRot} onChange={(v) => { setPhotoRot(v); setRenderedUrl(null); }} min={-180} max={180} step={1} suffix="°" />
          </div>

          <QuestionnaireReview questionnaire={questionnaire} />

          <div style={cardStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 12px" }}>Subjects &amp; Grades (8)</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {subjects.map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 8 }}>
                  <input data-testid={`preview-subject-${i + 1}`} value={s} onChange={(e) => { setSubject(i, e.target.value); setRenderedUrl(null); }} placeholder={`Subject ${i + 1}`} style={inputStyle} maxLength={40} />
                  <input data-testid={`preview-grade-${i + 1}`} value={grades[i]} onChange={(e) => { setGrade(i, e.target.value); setRenderedUrl(null); }} placeholder="Grade" style={{ ...inputStyle, textAlign: "center", fontWeight: 700 }} maxLength={6} />
                </div>
              ))}
            </div>
          </div>

          <div style={cardStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 12px" }}>Teacher Comments (4)</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Notes Font</label>
                <select value={noteFont} onChange={(e) => { setNoteFont(e.target.value); setRenderedUrl(null); }} style={inputStyle}>
                  <option value="comic">Comic Neue</option>
                  <option value="comic_bold_italic">Comic Neue Bold Italic</option>
                  <option value="roboto_slab">Roboto Slab</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Text Color</label>
                <input type="color" value={noteColor} onChange={(e) => { setNoteColor(e.target.value); setRenderedUrl(null); }} style={{ ...inputStyle, height: 40, padding: 4, cursor: "pointer" }} />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {comments.map((c, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <label style={labelStyle}>Comment {i + 1}</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "#6E6880" }}>
                        <span>Size</span>
                        <button type="button" aria-label={`Decrease comment ${i + 1} size`} onClick={() => setCommentFontSize(i, noteFontSizes[i] - 1)} disabled={noteFontSizes[i] <= 24} style={{ width: 30, height: 30, border: "1px solid #D8D3E6", borderRadius: 7, background: "#fff", color: "#2A2540", fontWeight: 800, fontSize: 18, lineHeight: 1, cursor: noteFontSizes[i] <= 24 ? "default" : "pointer", fontFamily: "inherit" }}>−</button>
                        <div data-testid={`preview-comment-size-${i + 1}`} style={{ minWidth: 42, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #E3E0EC", borderRadius: 7, background: "#fff", color: "#2A2540", fontWeight: 700, fontSize: 13 }}>{noteFontSizes[i]}</div>
                        <button type="button" aria-label={`Increase comment ${i + 1} size`} onClick={() => setCommentFontSize(i, noteFontSizes[i] + 1)} disabled={noteFontSizes[i] >= 60} style={{ width: 30, height: 30, border: "1px solid #D8D3E6", borderRadius: 7, background: "#fff", color: "#2A2540", fontWeight: 800, fontSize: 18, lineHeight: 1, cursor: noteFontSizes[i] >= 60 ? "default" : "pointer", fontFamily: "inherit" }}>+</button>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#6E6880", cursor: "pointer" }}>
                        <input type="checkbox" checked={noteUnderlines[i]} onChange={(e) => { setUnderline(i, e.target.checked); setRenderedUrl(null); }} /> Underline
                      </label>
                    </div>
                  </div>
                  <textarea data-testid={`preview-comment-${i + 1}`} value={c} onChange={(e) => { setComment(i, e.target.value); setRenderedUrl(null); }} rows={2} style={{ ...inputStyle, resize: "vertical", minHeight: 52, fontFamily: "inherit" }} maxLength={240} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ ...cardStyle, position: "sticky", top: 22, minHeight: 400 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 12px" }}>Rendered Report Card</h2>
          {error && <div data-testid="render-error" style={{ marginBottom: 10, padding: "10px 12px", background: "#FDECEA", border: "1px solid #F5C4BF", color: "#8E1F17", borderRadius: 8, fontSize: 13.5, whiteSpace: "pre-line", lineHeight: 1.5 }}>{error}</div>}
          {!renderedUrl && !busy && <div style={{ padding: "36px 12px", textAlign: "center", color: "#9A93B0", border: "1.5px dashed #DAD5E8", borderRadius: 10, fontSize: 14 }}><>Edit the report and click <strong>Render Preview</strong> to review it here, even while waiting for photos.</></div>}
          {busy && <div style={{ padding: "36px 12px", textAlign: "center", color: "#6A4FC0", fontSize: 14, fontWeight: 700 }}><Loader2 size={20} className="animate-spin" style={{ verticalAlign: "middle", marginRight: 8 }} />Rendering…</div>}
          {renderedUrl && <img src={renderedUrl} alt="Rendered report card" data-testid="rendered-report-image" style={{ width: "100%", height: "auto", border: "1px solid #ECEAF3", borderRadius: 8, display: "block" }} />}
        </div>
      </div>
      <div style={{ height: 40 }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .animate-spin { animation: spin 1s linear infinite; }`}</style>
    </main>
  );
}

export function QuestionnaireReview({ questionnaire }) {
  const q = questionnaire || {};
  const text = q.text || {};
  const list = (value) => Array.isArray(value) ? value.filter(Boolean).join(", ") : (value || "");
  const stories = Array.isArray(q.customer_stories) ? q.customer_stories.filter(Boolean).join("\n\n") : "";
  const rows = [
    ["Nickname(s)", text.nicknames],
    ["Species", list(q.species)],
    ["Boy or girl", list(q.gender)],
    ["Personality traits", list(q.personality_selections || q.traits)],
    ["Favorite activities", list(q.activity_selections || q.acts)],
    ["Favorite toy", text.favToy],
    ["Time with family", text.familyDuration],
    ["Funniest habit", text.funniestHabit],
    ["Tell Miss Paw-some More", q.tellMore || stories],
    ["Please Include", q.must_include || q.mustInclude],
  ];

  return (
    <div style={cardStyle} data-testid="customer-questionnaire-review">
      <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 4px" }}>Customer Questionnaire</h2>
      <p style={{ margin: "0 0 12px", color: "#8A8699", fontSize: 12.5 }}>Read-only customer answers for comparison with the editable subjects below.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ paddingBottom: 9, borderBottom: "1px solid #F0EDF6" }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: "#6E6880", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
            <div style={{ marginTop: 3, fontSize: 13.5, color: value ? "#2A2540" : "#9A93B0", whiteSpace: "pre-wrap", lineHeight: 1.45 }} data-testid={`questionnaire-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {value || "Not provided"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SliderRow({ label, testid, value, onChange, min, max, step, suffix = "", decimals = 0 }) {
  const displayVal = Number(value).toFixed(decimals) + suffix;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#6E6880", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
        <span data-testid={`${testid}-value`} style={{ fontSize: 12.5, fontWeight: 700, color: "#2A2540", fontVariantNumeric: "tabular-nums" }}>{displayVal}</span>
      </div>
      <input type="range" data-testid={testid} min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} style={{ width: "100%", accentColor: "#6A4FC0" }} />
    </div>
  );
}
